import { api } from './client';

// ── V113 — public registration gate status ──────────────────────────────
// Polled by Login + Signup pages on mount so we can render the
// gate-closed banner before the user fills in a doomed form.
// Public endpoint, no auth.
export const publicGateApi = {
  getRegistrationStatus: () =>
    api.get<{ enabled: boolean }>('/api/public/registration-status').then((r) => r.data),
};

// ── V115 — waitlist follow-up ───────────────────────────────────────────
export interface WaitlistSubmitInput {
  email: string;
  name: string;
  company?: string;
  useCase?: string;
}
export interface WaitlistEntry {
  id: string;
  email: string;
  name: string | null;
  company: string | null;
  useCase: string | null;
  /** pending | approved | rejected */
  status: string;
  createdAt: string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
}

export const waitlistApi = {
  // Public — no auth. 400 when gate is open, 409 on duplicate pending email.
  submit: (input: WaitlistSubmitInput) =>
    api.post<WaitlistEntry>('/api/public/waitlist', input).then((r) => r.data),
};

// ── V117 — Beta invite links ────────────────────────────────────────────
export interface BetaInviteLinkEntry {
  id: string;
  code: string;
  plan: string;
  maxUses: number | null;
  usesCount: number;
  expiresAt: string | null;
  createdBy: string;
  createdAt: string | null;
  disabledAt: string | null;
}
export interface BetaInviteRedemptionEntry {
  id: string;
  inviteLinkId: string;
  userId: string;
  redeemedAt: string;
}

export const inviteLinkApi = {
  // Public — no auth.
  check: (code: string) =>
    api
      .get<{
        valid: boolean;
        plan?: string;
        reason?: string;
      }>(`/api/public/invite-links/${code}/check`)
      .then((r) => r.data),
  // Authenticated. Idempotent (UNIQUE on link+user) so the FE
  // can retry on transient failures and a double-fire is safe.
  redeem: (code: string) =>
    api.post<{ ok: boolean }>('/api/invite-links/redeem', { code }).then((r) => r.data),
};

export const adminInviteLinkApi = {
  create: (body: { plan?: string; maxUses?: number | null; expiresAt?: string | null }) =>
    api.post<BetaInviteLinkEntry>('/api/admin/invite-links', body).then((r) => r.data),
  list: () => api.get<BetaInviteLinkEntry[]>('/api/admin/invite-links').then((r) => r.data),
  getDetail: (id: string) =>
    api
      .get<{
        link: BetaInviteLinkEntry;
        redemptions: BetaInviteRedemptionEntry[];
      }>(`/api/admin/invite-links/${id}`)
      .then((r) => r.data),
  disable: (id: string) =>
    api.patch<BetaInviteLinkEntry>(`/api/admin/invite-links/${id}/disable`).then((r) => r.data),
};

// ── V116 — Beta feedback widget ─────────────────────────────────────────
export type FeedbackCategory = 'bug' | 'feature_request' | 'general' | 'question';
export type FeedbackStatus = 'new' | 'acknowledged' | 'resolved' | 'wont_fix';

export interface SubmitFeedbackInput {
  category: FeedbackCategory;
  title: string;
  description: string;
  pageUrl?: string;
  /** Data URL or raw base64 — backend strips the `data:image/...;base64,` prefix. Max 5MB raw. */
  screenshotBase64?: string;
}

export interface FeedbackEntry {
  id: string;
  userId: string;
  workspaceId: string | null;
  category: FeedbackCategory;
  pageUrl: string | null;
  title: string;
  description: string;
  screenshotUrl: string | null;
  status: FeedbackStatus;
  adminNotes: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export const feedbackApi = {
  // Reads `beta_feedback_enabled` system_setting. Widget no-ops when false.
  config: () => api.get<{ enabled: boolean }>('/api/feedback/config').then((r) => r.data),
  submit: (input: SubmitFeedbackInput, workspaceId?: string) =>
    api
      .post<FeedbackEntry>('/api/feedback', input, {
        headers: workspaceId ? { 'X-Workspace-Id': workspaceId } : undefined,
      })
      .then((r) => r.data),
  listMine: () => api.get<FeedbackEntry[]>('/api/feedback').then((r) => r.data),
};

export const adminFeedbackApi = {
  list: (params?: { status?: FeedbackStatus; category?: FeedbackCategory; userId?: string }) =>
    api.get<FeedbackEntry[]>('/api/admin/feedback', { params }).then((r) => r.data),
  newCount: () => api.get<{ count: number }>('/api/admin/feedback/new-count').then((r) => r.data),
  getById: (id: string) => api.get<FeedbackEntry>(`/api/admin/feedback/${id}`).then((r) => r.data),
  update: (id: string, body: { status?: FeedbackStatus; adminNotes?: string }) =>
    api.patch<FeedbackEntry>(`/api/admin/feedback/${id}`, body).then((r) => r.data),
  delete: (id: string) => api.delete<void>(`/api/admin/feedback/${id}`).then((r) => r.data),
};

export const adminWaitlistApi = {
  list: (params?: { status?: string; search?: string }) =>
    api.get<WaitlistEntry[]>('/api/admin/waitlist', { params }).then((r) => r.data),
  pendingCount: () =>
    api.get<{ count: number }>('/api/admin/waitlist/pending-count').then((r) => r.data),
  approve: (id: string) =>
    api.patch<WaitlistEntry>(`/api/admin/waitlist/${id}/approve`).then((r) => r.data),
  reject: (id: string) =>
    api.patch<WaitlistEntry>(`/api/admin/waitlist/${id}/reject`).then((r) => r.data),
  bulkApprove: (ids: string[]) =>
    api.post<WaitlistEntry[]>('/api/admin/waitlist/bulk-approve', { ids }).then((r) => r.data),
};
